// Setting.jsx
import React, { useEffect, useState } from "react";
import { Container, Col, Row, Card, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import WebService from "../../Services/WebService";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import HelperService from "../../Services/HelperService";
import Loader from "../../components/Loader/Loader";

const Setting = () => {
  const {
    register,
    reset,
    watch,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false); // for initial fetch
  const [loader, setLoader] = useState(false); // for submit
  const [userData, setUserData] = useState({});
  const [permissions, setPermissions] = useState({
    can_create: false,
    can_read: false,
    can_update: false,
    can_delete: false,
  });
  useEffect(() => {
    getSettingListing();
  }, []);

  const getSettingListing = () => {
    setLoading(true);
    WebService.getAPI({
      action: `/setting/details`,
    })
      .then((res: any) => {
        setLoading(false);
        setUserData(res.result || {});
        // fill form values
        setValue("instagram_url", res.result?.instagram_url || "");
        setValue("email", res.result?.email || "");
        setValue("mobile", res.result?.mobile || "");
        setValue("cancelletion_fees", res.result?.cancelletion_fees || "");
        setValue(
          "max_demo_session_count",
          res.result?.max_demo_session_count || "",
        );
        setValue("exam_question_limit", res.result?.exam_question_limit || "");
        setValue("facebook_url", res.result?.facebook_url || "");
        setValue("twitter_url", res.result?.twitter_url || "");
        setValue("whatsapp_url", res.result?.whatsapp_url || "");
        setValue("youtube_url", res.result?.youtube_url || "");
        setValue("ios_latest_version", res.result?.ios_latest_version || "");
        setValue(
          "android_latest_version",
          res.result?.android_latest_version || "",
        );
      })
      .catch(() => {
        setLoading(false);
        //   toast.error("Failed to fetch settings.");
      });
  };

  // Regexes — balanced: strict enough to catch wrong domains, permissive enough for valid variants.
  const regex = {
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    instagram:
      /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/[A-Za-z0-9._-]+\/?$/i,
    facebook: /^(https?:\/\/)?(www\.)?facebook\.com\/[A-Za-z0-9.\-_/]+\/?$/i,
    twitter: /^(https?:\/\/)?(www\.)?twitter\.com\/([A-Za-z0-9_]{1,15})\/?$/i,
    whatsapp:
      /^(https?:\/\/)?(www\.)?(wa\.me|api\.whatsapp\.com)\/[A-Za-z0-9?=&+%_.-]+$/i,
    youtube:
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(channel|c|user|watch)|youtu\.be)\/[A-Za-z0-9_\-?=&%.+/]*$/i,
    phone: /^[0-9+\s-]{6,20}$/, // basic phone check (allows + and spaces)
  };

  const onSave = (data: any) => {
    // data keys match form register names
    const payload = {
      instagram_url: data.instagram_url || "",
      email: data.email || "",
      mobile: data.mobile || "",
      twitter_url: data.twitter_url || "",
      facebook_url: data.facebook_url || "",
      whatsapp_url: data.whatsapp_url || "",
      youtube_url: data.youtube_url || "",
      exam_question_limit: data.exam_question_limit || "",
      cancelletion_fees: data.cancelletion_fees,
      ios_latest_version: data.ios_latest_version || "",
      android_latest_version: data.android_latest_version || "",
    };

    setLoader(true);
    WebService.postAPI({
      action: `/setting/update`,
      body: payload,
    })
      .then((res: any) => {
        toast.success(res.message || "Settings updated");
        setLoader(false);
      })
      .catch((e) => {
        setError(true);
        setLoader(false);
        toast.error(e?.response?.data?.message || "Failed to update settings");
      });
  };

  return (
    <Container className="py-4">
      <form onSubmit={handleSubmit(onSave)}>
        <Row>
          <Col lg={12}>
            <h1 className="font-24 mb-4">
              <span>Settings</span>
            </h1>
          </Col>
        </Row>

        {/* ===== Social Media Section ===== */}
        <Loader show={loading} />
        <Row>
          <Col lg={8} sm={12} className="mx-auto">
            <Card className="p-4 shadow-sm rounded-4 mb-4">
              <h5 className="fw-bold mb-3">Social Media</h5>
              <Row>
                {/* Instagram */}
                <Col lg={12} className="mb-3">
                  <label>Instagram URL</label>
                  <input
                    id="instagram_url"
                    type="text"
                    className="form-control"
                    placeholder="Enter URL"
                    {...register("instagram_url", {
                      required: false,
                    })}
                  />
                </Col>

                {/* Facebook */}
                <Col lg={12} className="mb-3">
                  <label>Facebook URL</label>
                  <input
                    id="facebook_url"
                    type="text"
                    className="form-control"
                    placeholder="Enter URL"
                    {...register("facebook_url", {
                      required: false,
                    })}
                  />
                </Col>

                {/* WhatsApp */}
                <Col lg={12} className="mb-3">
                  <label>WhatsApp URL</label>
                  <input
                    id="whatsapp_url"
                    type="text"
                    className="form-control"
                    placeholder="Enter URL"
                    {...register("whatsapp_url", {
                      required: false,
                    })}
                  />
                </Col>

                {/* Twitter */}
                <Col lg={12} className="mb-3">
                  <label>Twitter URL</label>
                  <input
                    id="twitter_url"
                    type="text"
                    className="form-control"
                    placeholder="Enter URL"
                    {...register("twitter_url", {
                      required: false,
                    })}
                  />
                </Col>

                {/* YouTube */}
                <Col lg={12} className="mb-3">
                  <label>YouTube URL</label>
                  <input
                    id="youtube_url"
                    type="text"
                    className="form-control"
                    placeholder="Enter URL"
                    {...register("youtube_url", {
                      required: false,
                    })}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* ===== Contact Info Section ===== */}
        <Row>
          <Col lg={8} sm={12} className="mx-auto">
            <Card className="p-4 shadow-sm rounded-4">
              <h5 className="fw-bold mb-3">Contact Information</h5>
              <Row>
                {/* Mobile */}
                <Col lg={12} className="mb-3">
                  <label>Contact Number</label>
                  <input
                    id="mobile"
                    type="text"
                    className="form-control"
                    placeholder="Enter Contact Number"
                    {...register("mobile", {
                      required: "Please enter contact number",
                      pattern: {
                        value: regex.phone,
                        message: "Please enter a valid phone number",
                      },
                    })}
                    onKeyPress={(e: any) =>
                      HelperService.allowOnlyNumericValue(e)
                    }
                    maxLength={10}
                  />
                  {errors.mobile && (
                    <span className="text-danger small">
                      Please enter mobile number
                    </span>
                  )}
                </Col>

                {/* Email */}
                <Col lg={12} className="mb-3">
                  <label>Email ID</label>
                  <input
                    id="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter Email ID"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: regex.email,
                        message: "Please enter a valid email",
                      },
                    })}
                  />
                  {errors.email && (
                    <span className="text-danger small">
                      Please enter contact email
                    </span>
                  )}
                </Col>

                {/* Email */}
                <Col lg={12} className="mb-3">
                  <label>Live Exam Question Limit</label>
                  <input
                    id="exam_question_limit"
                    type="exam_question_limit"
                    className="form-control"
                    placeholder="Enter Questions Limit"
                    {...register("exam_question_limit", {
                      required: false,
                    })}
                  />
                  {errors.email && (
                    <span className="text-danger small">
                      Please enter questions limit
                    </span>
                  )}
                </Col>
                <Col lg={12} className="mb-3">
                  <label>Live Exam Cancelletion Fees</label>
                  <input
                    id="cancelletion_fees"
                    type="cancelletion_fees"
                    className="form-control"
                    placeholder="Enter Cancelletion Fees"
                    {...register("cancelletion_fees", {
                      required: false,
                    })}
                  />
                  {errors.email && (
                    <span className="text-danger small">
                      Please enter live cancelletion fees
                    </span>
                  )}
                </Col>

                <Col lg={12} className="mb-3">
                  <label>IOS Version</label>
                  <input
                    id="ios_latest_version"
                    type="text"
                    className="form-control"
                    placeholder="Enter IOS Latest Version"
                    {...register("ios_latest_version", {
                      required: false,
                    })}
                  />
                </Col>
                <Col lg={12} className="mb-3">
                  <label>Android Version</label>
                  <input
                    id="android_latest_version"
                    type="text"
                    className="form-control"
                    placeholder="Enter Android Latest Version"
                    {...register("android_latest_version", {
                      required: false,
                    })}
                  />
                </Col>

                {/* Submit Button */}
                <Col lg={12} className="text-center mt-2">
                  <button type="submit" className="btn btn-brand-1 w-25">
                    {loader ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      "Update"
                    )}
                  </button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default Setting;
